import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { utils, write } from 'xlsx';
import { saveAs } from 'file-saver';
import useHandleErrors from '../../utils/user.utils';
import jsPDF from 'jspdf';
import wwImg from '../../assets/globalImgs/WW-360-Logo.png'
import { useUser } from '../../utils/userContext';
import moment from 'moment';

const ClaimReport = () => {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [file, setFile] = useState([]);
    const [fileName, setFileName] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const { user } = useUser();
    const hasFetchedRef = useRef(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        ClaimId: "",
        EmployeeId: "",
        UpdatedBy: user?.FirstName,
        Comments: "",
        Status: ""
    });
    const handleErrors = useHandleErrors();
    const storedToken = localStorage.getItem("token");


    const getClaimStatus = async () => {
        setLoading(true)
        const path = "/getSubmitClaimsDetails";
        const URL = process.env.REACT_APP_BE_URL + path;
        try {
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${storedToken}`,
                },
            })
            if (response.status === 200) {
                const responseData = response.data.listSubmitClaims || [];
                setData(responseData);
                setFilteredData(responseData);
            }
            else {
                handleErrors(response)
            }

        } catch (error) {
            if (error.response) {
                if (error.response.status === 404) {
                    // Handle 404 status code separately
                    setData([]);
                    setFilteredData([]);
                } else {
                    handleErrors(error.response);
                }
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!hasFetchedRef.current) {
            getClaimStatus()
            hasFetchedRef.current = true;
        }
    }, []);


    const handleCancel = (e) => {
        e.preventDefault();
        setFormData({ ClaimId: '', Comments: '', EmployeeId: '', Status: '' });
        setFile([]);
        setFileName("");
        const radioButtons = document.getElementsByName('updateClaim');
        radioButtons.forEach((radioButton) => {
            radioButton.checked = false;
        });

    };

    useEffect(() => {
        // Filter employees based on the search query
        setFilteredData(
            data.filter(data =>
                data.ClaimId.toLowerCase().includes(searchQuery.toLowerCase()) ||
                data.Category.toLowerCase().includes(searchQuery.toLowerCase()) ||
                data.ClaimAmount.toLowerCase().includes(searchQuery.toLowerCase()) ||
                data.Status.toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [searchQuery, data]);

    const sendEmail = (email) => {
        // Excel generation logic
        const ws = utils.json_to_sheet(filteredData);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Claims');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });

        // Convert the Excel file to a Base64 string
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const reader = new FileReader();
        reader.readAsDataURL(data);
        reader.onloadend = () => {
            const base64data = reader.result.split(',')[1]; // Get the Base64 string without the prefix

            // Create a downloadable link for the attachment
            const link = document.createElement('a');
            link.href = reader.result;
            link.download = 'ClaimReport.xlsx';

            // Simulate a click to download the attachment
            link.click();

            // Prepare the mailto link (without attachment)
            const subject = "Assigned Asset List";
            const body = 'Please find the attached file.';
            const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

            toast.info('The Excel file has been generated and downloaded. Please attach the file manually to your email.', {
                position: toast.POSITION.TOP_CENTER,
            });

            setTimeout(() => {
                window.location.href = mailtoLink;
            }, 3000);

        };
    };

    const handleExport = () => {
        const ws = utils.json_to_sheet(filteredData);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Assets');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const today = new Date();
        const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year
        const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
        const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
        const formattedDate = `${year}${month}${day}`;
        saveAs(data, `WW360-Claims_Report_${formattedDate}.xlsx`);
    };
    const shareData = {
        title: 'Claim List',
        text: 'Check out the Claim list',
        url: window.location.href
    };

    const handleShare = async () => {
        try {
            await navigator.share(shareData);
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    const handleDropdownClick = (record) => {
        setFormData({
            ...formData,
            ClaimId: record?.ClaimId,
            EmployeeId: record?.EmployeeId
        });
        setIsDropdownOpen(true);
    };

    const handleCommentChange = (event) => {
        setFormData({ ...formData, Comments: event.target.value });
    };

    const handleDownloadPDF = (record) => {
        const doc = new jsPDF();

        // Define page margins and dimensions
        const marginLeft = 10;
        const marginRight = 10;
        const pageWidth = doc.internal.pageSize.width;

        // Header
        // Add logo on the left side
        const imgData = wwImg; // Replace with your actual logo path or base64 string
        doc.addImage(imgData, 'PNG', marginLeft, 10, 40, 15); // Logo positioned on the left

        // Add timestamp on the right side in small size
        const timestamp = new Date().toLocaleString();
        doc.setFontSize(8);
        doc.text(timestamp, pageWidth - marginRight, 15, { align: 'right' });

        // Add heading
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(37, 183, 64); // Set text color to #25b740 (RGB: 37, 183, 64)
        doc.text("Expense Form", pageWidth / 2, 30, { align: 'center' });

        // Reset text color to black for other text
        doc.setTextColor(0, 0, 0);

        // Add employee details
        doc.setFontSize(10);
        doc.setFont("helvetica", "Bold");
        doc.setTextColor(0, 79, 124); // Set text color to #004f7c (RGB: 0, 79, 124)
        const employeeDetailsStartY = 45; // Start Y position for employee details
        doc.text("Employee Details", marginLeft, employeeDetailsStartY);
        doc.setTextColor(0, 0, 0);
        // Employee details
        const employeeDetails = [
            { label: 'Employee ID', value: record.EmployeeId },
            { label: 'Employee Email', value: record.EmailId }
        ];

        // Draw employee details
        employeeDetails.forEach((detail, index) => {
            const y = employeeDetailsStartY + 10 + (index * 10);
            doc.text(`${detail.label}: ${detail.value}`, marginLeft, y);
        });

        // Draw a thin line above the table
        const lineY = employeeDetailsStartY + 10 + (employeeDetails.length * 10) + 5;
        doc.setDrawColor(0, 0, 0);
        // doc.setLineWidth(0.5); // Standard line width
        doc.line(marginLeft, lineY, pageWidth - marginRight, lineY);

        // Add "Details" heading
        const detailsHeadingY = lineY + 10; // Y position for "Details" heading
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 79, 124); // Set text color to #004f7c (RGB: 0, 79, 124)
        doc.text("Claim Details", marginLeft, detailsHeadingY);

        // Define table headers
        const headers = ['Field', 'Value'];
        doc.setTextColor(0, 0, 0);
        const rows = [
            { key: 'Claim ID', value: `${record.ClaimId}` },
            { key: 'Type of Category', value: `${record.TypeOfCategory?record.TypeOfCategory:"N/A"}` },
            { key: 'Claim Category', value: `${record.Category}` },
            { key: 'Billing Date', value: moment(`${record.BillingStartDate}`).format("DD-MM-YYYY") },
            { key: 'Claim Amount', value: `${record.ClaimAmount}` },
            { key: 'Approved By', value: `${record.ApprovedBy?record.ApprovedBy:"N/A"}` },
            { key: 'Paid By', value: `${record.PaidBy?record.PaidBy:"N/A"}` },
            { key: 'Paid To', value: `${record.PaidTo?record.PaidTo:"N/A"}` },
            { key: 'Status', value: `${record.Status}` },
        ];

        // Define table positions and dimensions
        const tableStartY = detailsHeadingY + 20; // Add space below "Details" heading
        const rowHeight = 10;
        const columnWidth = {
            field: 80,
            value: pageWidth - 100 // Adjust as needed to fit the page width
        };

        // Draw table headers (Field and Value)
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setDrawColor(0, 0, 0);
        // doc.setLineWidth(0.5); // Normal line width

        // Draw header cell borders
        doc.rect(marginLeft, tableStartY - rowHeight, columnWidth.field, rowHeight); // Field header cell border
        doc.rect(marginLeft + columnWidth.field, tableStartY - rowHeight, columnWidth.value, rowHeight); // Value header cell border

        // Add header text
        doc.text(headers[0], marginLeft + 5, tableStartY - rowHeight / 2 + 3); // "Field" header text
        doc.text(headers[1], marginLeft + columnWidth.field + 5, tableStartY - rowHeight / 2 + 3); // "Value" header text

        // Reset font to normal for table data rows
        doc.setFont("helvetica", "normal");

        // Draw table rows
        rows.forEach((row, index) => {
            const y = tableStartY + (index * rowHeight) + rowHeight / 2; // Adjusted to center the text vertically
            doc.text(row.key, marginLeft + 5, y); // Field value
            doc.text(row.value, marginLeft + columnWidth.field + 25, y, { align: 'right' }); // Value
            // Draw row borders
            doc.setDrawColor(0, 0, 0);
            doc.rect(marginLeft, y - rowHeight / 2, columnWidth.field, rowHeight); // Row border
            doc.rect(marginLeft + columnWidth.field, y - rowHeight / 2, columnWidth.value, rowHeight); // Row border
        });

        // Add "Approved By" and "Name" text
        const approvedByY = tableStartY + (rows.length * rowHeight) + 20; // Position below the table
        const nameText = `${record.ApprovedBy}`;
        const approvedByText = "Approved By";

        // Add the text aligned to the right side
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");

        // "Name" text
        doc.text(nameText, pageWidth - marginRight, approvedByY, { align: 'right' });
        doc.text(approvedByText, pageWidth - marginRight, approvedByY + 10, { align: 'right' });

        // Add footer with timestamp and additional data, aligned left and right
        doc.setFontSize(10);
        const footerTimestamp = `Timestamp: ${timestamp}`;
        const footerText = "Generated By WorkWise360";
        doc.text(footerTimestamp, marginLeft, 280); // Left-aligned footer
        doc.text(footerText, pageWidth - marginRight, 280, { align: 'right' }); // Right-aligned footer


        // Save the PDF
        doc.save(`Claim_${record.ClaimId}.pdf`);
    };

    useEffect(() => {
        const preloading = document.getElementById("preloading");
        setTimeout(() => {
            if (preloading) {
                preloading.style.display = "none";
            }
        }, 400);
    }, []);


    const handleRadioClick = (event) => {
        setFormData({ ...formData, Status: event.target.id });
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        if (!formData.Status) {
            toast.error("Please Select Status.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return setLoading(false);
        }
        try {
            const path = "/actionSubmitClaims";
            const URL = process.env.REACT_APP_BE_URL + path;

            const payload = {
                ClaimId: formData.ClaimId,
                Comments: formData.Comments,
                EmployeeId: formData.EmployeeId,
                Status: formData.Status,
                ApprovedBy: user?.EmployeeId,
                UpdatedBy: user?.EmployeeId,
            };

            const response = await axios.put(URL, payload, {
                headers: {
                    Authorization: `Bearer ${storedToken}`,
                },
            });

            if (response.status === 200) {
                // setIsDropdownOpen(false);
                setFormData({ ClaimId: '', Comments: '', EmployeeId: '', Status: '' });
                getClaimStatus();
                toast.success("Claims Updated successfully!", {
                    position: toast.POSITION.TOP_CENTER,
                });
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadAll = (documents) => {
        documents.forEach(doc => {
            const link = document.createElement('a');
            const fileName = decodeURIComponent(doc.substring(doc.lastIndexOf('/') + 1));
            link.href = doc;
            link.download = fileName;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        });
    };
    const handleRecordsPerPageChange = (event) => {
        setRecordsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredData.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredData.length / recordsPerPage);


    const paginate = pageNumber => setCurrentPage(pageNumber);

    return (
        <div>
            <div>
                {loading && (
                    <div id="preloading">
                        <span className="loader"></span>
                    </div>
                )}
                <div className="content-page">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col">
                                <h3 className="title mb-2">Claims Report</h3>
                            </div>
                            <div className="col-auto">
                                <Link to={'/SubmitTimesheet'} className="btn btn-primary mt-lg-0 mt-4">
                                    <i className="ri-add-circle-line"></i>Timesheet
                                </Link>
                            </div>
                        </div>
                        {data?.length === 0 ? (
                            <div class="container">
                                <div class="row vh-50 justify-content-center align-items-center">
                                    <div class="col-auto">
                                        <div class="no-data-wrapper">
                                            <div class="no-data-found">
                                                <i class="ri-file-damage-fill"></i>
                                                <div class="bounce"><i class="ri-search-eye-line"></i></div>
                                                <p>No Data Found</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div class="row align-items-center justify-content-center">
                                    <div class="col-5">
                                        <div class="container-fluid row-rounded mb-0">
                                            <div class="row px-2 py-0">
                                                <div class="input-group mb-0">
                                                    <input
                                                        type="search"
                                                        className="form-control search-input"
                                                        placeholder="Keyword search..."
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                    />
                                                    <div class="input-group-append">
                                                        <button class="btn btn-primary" type="button" title="Search" rel="tooltip"><i class="ri-menu-search-line"></i></button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <small class="form-text text-muted text-center mt-0"><i class="ri-information-2-fill text-info"></i> Allows: Category - ClaimId - Claim Amount - Status </small>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-lg-12">
                                        <div className="card mb-4">
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col">
                                                        <h5 className="title mt-1 mb-0">Report</h5>
                                                    </div>
                                                    <div className="col-auto">
                                                        <button type="button" title="Email" className="btn btn-light btn-circle ml-2" onClick={() => sendEmail('')}>
                                                            <i className="ri-mail-send-fill"></i>
                                                        </button>
                                                        <button type="button" title="Share" className="btn btn-light btn-circle ml-2" onClick={handleShare}>
                                                            <i className="ri-share-fill"></i>
                                                        </button>
                                                        <button type="button" title="Export" className="btn btn-light btn-circle ml-2" onClick={handleExport}>
                                                            <i className="ri-export-fill"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="row mt-3">
                                                    <div className="col-md-12">
                                                        <div className="table-responsive asset-information">
                                                            <table className="table grid">
                                                                <thead className="bg-light">
                                                                    <tr>
                                                                        <th>S.No.</th>
                                                                        <th>Emp ID</th>
                                                                        <th>Emp Name</th>
                                                                        <th>Claim ID</th>
                                                                        <th>Type of Category</th>
                                                                        <th>Category</th>
                                                                        <th>Billing Date</th>
                                                                        <th>Amount</th>
                                                                        <th>Status</th>
                                                                        <th></th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {currentRecords?.map((record, index) => (
                                                                        <tr key={record.ClaimId}>
                                                                            <td>{index + 1 + indexOfFirstRecord}</td>
                                                                            <td>{record.EmployeeId}</td>
                                                                            <td>{record.EmployeeName}</td>
                                                                            <td>{record.ClaimId}</td>
                                                                            <td>{record.TypeOfCategory?record.TypeOfCategory:"N/A"}</td>
                                                                            <td>{record.Category}</td>
                                                                            <td>{new Date(record?.BillingStartDate).toLocaleDateString()}</td>
                                                                            <td>{record.ClaimAmount}</td>
                                                                            <td>
                                                                                {record.Status.trim().toLowerCase() === 'approved' ? (
                                                                                    <span className="badge border border-success text-success status">
                                                                                        <i className="ri-thumb-up-fill"></i> {record.Status.trim()}
                                                                                    </span>
                                                                                ) : record.Status.trim().toLowerCase() === 'rejected' ? (
                                                                                    <span className="badge border border-danger text-danger status">
                                                                                        <i className="ri-thumb-down-line"></i> {record.Status.trim()}
                                                                                    </span>
                                                                                ) : record.Status.trim().toLowerCase() === 'pending' ? (
                                                                                    <span className="badge border border-warning text-warning status">
                                                                                        <i className="ri-hourglass-fill"></i> {record.Status.trim()}
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="badge border border-secondary text-secondary status">
                                                                                        <i className="ri-thumb-down-line"></i> {record.Status.trim()}
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                            <td className="text-center">
                                                                                <span>
                                                                                    <button
                                                                                        className="btn btn-link text-info btn-action custom-dropdown-btn"
                                                                                        type="button"
                                                                                        title="Documents"
                                                                                        data-toggle="dropdown"
                                                                                    >
                                                                                        <i className="ri-file-2-line"></i>
                                                                                    </button>
                                                                                    <div id={`viewDocuments-${record.ClaimId}`} className="dropdown-menu dropdown dropdown-menu-right custom-dropdown shadow">
                                                                                        <div className="row">
                                                                                            <div className="col-12 mb-2 border-bottom">
                                                                                                <h6>Claim ID: {record.ClaimId}</h6>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="row drop-down-scroll">
                                                                                            {record?.ClaimDocument?.map((doc, index) => {
                                                                                                const fileName = decodeURIComponent(doc.substring(doc.lastIndexOf('/') + 1))?.slice(0, 15);
                                                                                                return (
                                                                                                    <div className="col-12 mb-2" key={index}>
                                                                                                        <button className="btn btn-link text-danger btn-action" type="button">
                                                                                                            <i className="ri-eye-line"></i>
                                                                                                        </button>
                                                                                                        <a 
                                                                                                            href={doc} 
                                                                                                            target="_blank" 
                                                                                                            rel="noopener noreferrer" 
                                                                                                            className="filename"
                                                                                                            onClick={(e) => e.stopPropagation()}
                                                                                                        >
                                                                                                            {fileName}
                                                                                                        </a>
                                                                                                    </div>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                        <div className="row">
                                                                                            <div className="col-12 text-center">
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className="btn btn-primary"
                                                                                                    onClick={() => handleDownloadAll(record?.ClaimDocument)}
                                                                                                >
                                                                                                    Download All <i className="ri-file-download-line"></i>
                                                                                                </button>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </span>

                                                                                {record?.Status.trim().toLowerCase() === 'pending' || record?.Status.trim().toLowerCase() === 'onhold' ? (
                                                                                    <span>
                                                                                        <button
                                                                                            className="btn btn-link text-info btn-action custom-dropdown-btn"
                                                                                            type="button"
                                                                                            title="Update"
                                                                                            data-toggle="dropdown"
                                                                                            onClick={() => handleDropdownClick(record)}
                                                                                        >
                                                                                            <i className="ri-list-settings-line"></i>
                                                                                        </button>
                                                                                        {isDropdownOpen ?
                                                                                            <div id={`updateClaims-${record.ClaimId}`} className="dropdown-menu dropdown dropdown-menu-right custom-dropdown shadow">
                                                                                                <div className="row">
                                                                                                    <div className="col-12 mb-2 border-bottom">
                                                                                                        <h6>Claim ID: {record.ClaimId}</h6>
                                                                                                    </div>
                                                                                                </div>
                                                                                                <div className="row drop-down-scroll">
                                                                                                    <div className="form-group col-6">
                                                                                                        <div className="form-check">
                                                                                                            <input
                                                                                                                className="form-check-input"
                                                                                                                type="radio"
                                                                                                                name="updateClaim"
                                                                                                                id="Approved"
                                                                                                                onClick={handleRadioClick}
                                                                                                            />
                                                                                                            <label className="form-check-label" htmlFor="Approved">
                                                                                                                Approve
                                                                                                            </label>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                    <div className="form-group col-6">
                                                                                                        <div className="form-check">
                                                                                                            <input
                                                                                                                className="form-check-input"
                                                                                                                type="radio"
                                                                                                                name="updateClaim"
                                                                                                                id="Rejected"
                                                                                                                onClick={handleRadioClick}
                                                                                                            />
                                                                                                            <label className="form-check-label" htmlFor="Rejected">
                                                                                                                Reject
                                                                                                            </label>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                    <div className="form-group col-12">
                                                                                                        <div className="form-check">
                                                                                                            <input
                                                                                                                className="form-check-input"
                                                                                                                type="radio"
                                                                                                                name="updateClaim"
                                                                                                                id="OnHold"
                                                                                                                onClick={handleRadioClick}
                                                                                                            />
                                                                                                            <label className="form-check-label" htmlFor="notifyAskmore">
                                                                                                                Ask more details
                                                                                                            </label>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                    <div className="form-group col-12">
                                                                                                        <label htmlFor="notifyComment">Comment</label>
                                                                                                        <textarea
                                                                                                            className="form-control"
                                                                                                            rows="3"
                                                                                                            cols="5"
                                                                                                            value={formData.Comments}
                                                                                                            onChange={handleCommentChange}
                                                                                                        ></textarea>
                                                                                                    </div>
                                                                                                </div>
                                                                                                <div className="row">
                                                                                                    <div className="col-12 text-center">
                                                                                                        <button type="reset" className="btn btn-light" onClick={handleCancel}>Cancel</button>
                                                                                                        <button type="button" className="btn btn-primary" onClick={handleSubmit}>Update <i className="ri-checkbox-circle-line"></i></button>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div> : null}
                                                                                    </span>
                                                                                ) : (
                                                                                    <button
                                                                                        className="btn btn-link text-info btn-action custom-dropdown-btn"
                                                                                        type="button"
                                                                                        title="Download"
                                                                                        rel="tooltip"
                                                                                        onClick={() => handleDownloadPDF(record)}
                                                                                    >
                                                                                        <i className="ri-download-2-line"></i>
                                                                                    </button>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    ))}

                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-12">
                                                        <div className="container-fluid">
                                                            <div className="row border-top pt-3">
                                                                <div className="col-auto">
                                                                    <p className="mt-1 mb-0">Records</p>
                                                                </div>
                                                                <div className="col-1 record-drop">
                                                                    <select className="form-control" value={recordsPerPage} onChange={handleRecordsPerPageChange}>
                                                                        <option value="5">5</option>
                                                                        <option value="10">10</option>
                                                                        <option value="15">15</option>
                                                                        <option value="20">20</option>
                                                                        <option value="25">25</option>
                                                                    </select>
                                                                </div>
                                                                <div className="col">
                                                                    <nav>
                                                                        <ul className="pagination justify-content-center">
                                                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                                <button className="page-link" onClick={() => paginate(1)} tabIndex="-1">
                                                                                    <i className="ri-arrow-left-double-line"></i>
                                                                                </button>
                                                                            </li>
                                                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                                <button className="page-link" onClick={() => paginate(currentPage - 1)} tabIndex="-1">
                                                                                    <i className="ri-arrow-left-s-line"></i>
                                                                                </button>
                                                                            </li>
                                                                            {Array.from({ length: totalPages }, (_, index) => (
                                                                                <li key={index + 1} className={`page-item ${index + 1 === currentPage ? 'active' : ''}`}>
                                                                                    <button className="page-link" onClick={() => paginate(index + 1)}>
                                                                                        {index + 1}
                                                                                    </button>
                                                                                </li>
                                                                            ))}
                                                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                                <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                                                                                    <i className="ri-arrow-right-s-line"></i>
                                                                                </button>
                                                                            </li>
                                                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                                <button className="page-link" onClick={() => paginate(totalPages)}>
                                                                                    <i className="ri-arrow-right-double-fill"></i>
                                                                                </button>
                                                                            </li>
                                                                        </ul>
                                                                    </nav>
                                                                </div>
                                                                <div className="col-auto">
                                                                    <p className="mt-1 mb-0">Page Showing {currentPage} of {totalPages}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <ToastContainer />
            </div>
        </div>
    )
}

export default ClaimReport
